import { useState, type Ref } from 'react';
import type { TRPCOptionsProxy, inferOutput } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@servwell/api/router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PRICING_BASES, PRICING_BASIS_LABEL, type PricingBasis } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { PressedGroup } from '../ui/PressedGroup';
import { awaitingServer, mutationState } from '../ui/QueryStates';

/**
 * How a job bills the parts used on it (CONTEXT.md, "Pricing basis"; Inventory v2 Phase 1,
 * ticket 01). The server decides it (`pricingBasisOf`) and says why; this file only shows it,
 * and lets the office change one job's (prototype verdict A: the same pattern as Service type,
 * a "Change" in the strip that opens a card below).
 */

/**
 * The job's pricing as `job.get` answers it, typed by the router: the basis, why, whether it is
 * locked, and the type's basis now. No money in it. `locked` is the SERVER's word on whether the
 * office may change it (`pricingBasisOf`); this file never decides that itself.
 */
export type JobPricingAnswer = NonNullable<
  inferOutput<TRPCOptionsProxy<AppRouter>['job']['get']>
>['pricing'];

/** The line under the basis: why it applies, and when the type now says otherwise. */
export function pricingReason(p: JobPricingAnswer, typeLabel: string | null): string {
  switch (p.reason) {
    case 'agreement':
      return 'An agreement visit: always Fixed.';
    case 'quote':
      return 'From an accepted quote: a quoted job is always Fixed.';
    case 'booked': {
      // Stamped at booking; a later flip of the type leaves the job alone, and says so.
      const differs = p.typeBasis !== null && p.typeBasis !== p.basis;
      if (!typeLabel) return 'Booked before service types existed.';
      return differs
        ? `From its service type when it was booked. ${typeLabel} is ${PRICING_BASIS_LABEL[p.typeBasis!]} now.`
        : `From its service type, ${typeLabel}.`;
    }
    case 'changed': {
      const differs = p.typeBasis !== null && p.typeBasis !== p.basis;
      return differs && typeLabel
        ? `Changed by the office. Its type, ${typeLabel}, is ${PRICING_BASIS_LABEL[p.typeBasis!]}.`
        : 'Changed by the office.';
    }
  }
}

/** The strip's value: the basis, the office's Change (unless locked), and the reason under it. */
export function JobPricingValue({
  pricing,
  typeLabel,
  canChange,
  open,
  buttonRef,
  onToggle,
}: {
  pricing: JobPricingAnswer;
  typeLabel: string | null;
  canChange: boolean;
  open: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  onToggle: () => void;
}) {
  return (
    <span className="inline-flex flex-col">
      <span className="inline-flex items-center gap-2">
        {PRICING_BASIS_LABEL[pricing.basis]}
        {canChange && !pricing.locked ? (
          <Button
            variant="ghost"
            ref={buttonRef}
            aria-label="Change how parts are priced"
            aria-expanded={open}
            onClick={onToggle}
          >
            Change
          </Button>
        ) : null}
      </span>
      <span className="text-xs font-normal text-muted-foreground">
        {pricingReason(pricing, typeLabel)}
      </span>
    </span>
  );
}

/** The card the office's Change opens: Fixed | T&M, then Save. One write, then it closes. */
export function ChangePricingCard({
  jobId,
  current,
  onClose,
}: {
  jobId: string;
  current: PricingBasis;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<PricingBasis>(current);
  const save = useMutation(
    trpc.job.setPricingBasis.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: trpc.job.get.queryKey({ jobId }) });
        onClose();
      },
    }),
  );
  return (
    <SectionCard title="Change how parts are priced">
      <p className="mb-3 text-sm text-muted-foreground">
        <strong className="text-text">Fixed:</strong> the job&apos;s price covers its
        parts; any used beyond the quote show on the Money tab.{' '}
        <strong className="text-text">T&amp;M:</strong> each part used bills at its
        price-book price. The job&apos;s invoice picks up the parts used so far; a part
        used after it waits under Left to bill until you add it. Changing it doesn&apos;t
        rewrite the draft; parts already used show under the new basis.
      </p>
      {save.error ? (
        <Notice tone="danger" alert>
          {save.error.message}
        </Notice>
      ) : null}
      <PressedGroup
        label="How this job's parts are priced"
        options={PRICING_BASES.map((value) => ({
          value,
          label: PRICING_BASIS_LABEL[value],
        }))}
        value={picked}
        onChange={setPicked}
      />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          aria-disabled={awaitingServer(save)}
          onClick={() => {
            if (awaitingServer(save)) return;
            if (picked === current) return onClose();
            save.mutate({ jobId, basis: picked, ...idem() });
          }}
        >
          {mutationState(save) === 'queued'
            ? 'Waiting to send'
            : mutationState(save) === 'sending'
              ? 'Saving…'
              : 'Save'}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </SectionCard>
  );
}
